import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { publicApi } from "@/lib/api";

const AUTO_MS = 6000;

function Stars({ rating = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i <= rating ? "fill-[#FF4500] text-[#FF4500]" : "text-white/20"}`}
        />
      ))}
    </div>
  );
}

export default function ReviewsStrip() {
  const [reviews, setReviews] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    publicApi
      .listTopReviews({ limit: 8, min_rating: 5 })
      .then((data) => setReviews(Array.isArray(data) ? data : []))
      .catch(() => setReviews([]));
  }, []);

  useEffect(() => {
    if (reviews.length <= 1 || paused) return;
    timerRef.current = window.setInterval(() => {
      setIdx((i) => (i + 1) % reviews.length);
    }, AUTO_MS);
    return () => window.clearInterval(timerRef.current);
  }, [reviews.length, paused]);

  if (!reviews.length) return null;

  const prev = () => setIdx((i) => (i - 1 + reviews.length) % reviews.length);
  const next = () => setIdx((i) => (i + 1) % reviews.length);
  const current = reviews[idx];

  return (
    <section
      data-testid="section-reviews-strip"
      className="border-y border-white/10 bg-[#0F0F0F]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="overline mb-3">Straight from the studio&nbsp;·&nbsp;5-star reviews</p>
            <h2 className="font-display text-4xl md:text-6xl leading-none">
              Real words from <span className="text-[#FF4500]">real collectors</span>.
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="reviews-strip-prev"
              onClick={prev}
              aria-label="Previous review"
              className="w-11 h-11 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              data-testid="reviews-strip-next"
              onClick={next}
              aria-label="Next review"
              className="w-11 h-11 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <article
          key={current.id}
          data-testid="reviews-strip-card"
          className="relative border border-white/10 bg-[#141414] p-8 md:p-12 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center animate-in fade-in slide-in-from-bottom-2 duration-500"
        >
          <Quote className="absolute top-6 right-6 w-14 h-14 text-[#FF4500]/15" />
          {current.product_image && (
            <Link
              to={`/shop/${current.product_id}`}
              data-testid="reviews-strip-product-link"
              className="block w-28 h-28 md:w-40 md:h-40 shrink-0 overflow-hidden border border-white/10 group"
            >
              <img
                src={current.product_image}
                alt={current.product_title || "Reviewed artwork"}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </Link>
          )}
          <div className="min-w-0">
            <Stars rating={current.rating} />
            <blockquote className="mt-4 font-display text-2xl md:text-3xl leading-tight">
              &ldquo;{current.body}&rdquo;
            </blockquote>
            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/60">
              <span data-testid="reviews-strip-author" className="text-white uppercase tracking-widest">
                {current.author_name}
              </span>
              {current.product_title && (
                <Link
                  to={`/shop/${current.product_id}`}
                  className="hover:text-[#FF4500] transition-colors"
                >
                  on <em className="not-italic underline decoration-white/20 underline-offset-4">{current.product_title}</em>
                </Link>
              )}
            </div>
          </div>
        </article>

        {reviews.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2" data-testid="reviews-strip-dots">
            {reviews.map((r, i) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to review ${i + 1}`}
                className={`h-1.5 transition-all ${i === idx ? "w-8 bg-[#FF4500]" : "w-4 bg-white/20 hover:bg-white/40"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
